import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import AppContext from '../common/containers/AppContext';
import Loading from './components/Loading';
import getPodNames from './services/data/getPodNames';
import useGetRequest from '../common/services/useGetRequest';
import getCustomers from '../common/services/data/getCustomers';
import { CustomerStatus, FamilyStatus } from './types';
import getFamilies from './services/data/getFamilies';
import getPermissionsToFamilies from './services/permissions/getPermissionsToFamilies';
import hasPermission from './services/permissions/hasPermission';
import getRecentlySearchedItems from './services/data/getRecentlySearchedItems';
import PageGroupType from './components/PageGroupType';
import AppStatsPageContent from './components/AppStatsPageContent';

AppStatsPageContent.propTypes = { children: PropTypes.node };
export default function AppStatsAuthenticatedContainer() {
    const [user, userIsLoading] = useGetRequest('/ui/session/userinfo');
    const [customers, setCustomers] = useState(null);
    const [families, setFamilies] = useState(null);
    const [recentlySearchedIdsByType, setRecentlySearchedIdsByType] = useState(null);
    const [dateRangeSelectedOption, setDateRangeSelectedOption] = useState('Daily');
    const [podNames, setPodNames] = useState(null);

    // Customer Flex options (for filtering)
    const [regions, setRegions] = useState(null);
    const [customerSizes, setCustomerSizes] = useState(null);
    const [industries, setIndustries] = useState(null);
    const [segments, setSegments] = useState(null);

    const renderPageContent = useCallback(
        (children) => <AppStatsPageContent>{children}</AppStatsPageContent>,
        [],
    );

    useEffect(() => {
        (async () => {
            const [
                initialCustomers,
                initialFamilies,
                initialRecentlySearchedFamilyIds,
                initialRecentlySearchedCustomerIds,
                initialRecentlySearchedPlatformIds,
                initialPods,
            ] = await Promise.all([
                getCustomers(),
                getFamilies({ status: FamilyStatus.ACTIVE }),
                getRecentlySearchedItems(PageGroupType.FAMILY),
                getRecentlySearchedItems(PageGroupType.CUSTOMER),
                getRecentlySearchedItems(PageGroupType.PLATFORM),
                getPodNames(),
            ]);

            const nonMergedCustomers = initialCustomers.filter(
                ({ status }) => status !== CustomerStatus.MERGED,
            );
            setCustomers(nonMergedCustomers);

            const regionList = new Set();
            const customerSizeList = new Set();
            const industryList = new Set();
            const segmentList = new Set();

            nonMergedCustomers.forEach((customer) => {
                if (customer.flex) {
                    customer.flex.region?.forEach((r) => {
                        regionList.add(r);
                    });
                    customer.flex.size?.forEach((s) => {
                        customerSizeList.add(s);
                    });
                    customer.flex.industry?.forEach((i) => {
                        industryList.add(i);
                    });
                    if (customer.flex?.customer_segment) {
                        segmentList.add(customer.flex.customer_segment);
                    }
                }
            });
            setRegions(regionList);
            setCustomerSizes(customerSizeList);
            setIndustries(industryList);
            setSegments(segmentList);

            setFamilies(initialFamilies);
            setRecentlySearchedIdsByType({
                platform: initialRecentlySearchedPlatformIds,
                family: initialRecentlySearchedFamilyIds,
                customer: initialRecentlySearchedCustomerIds,
            });
            setPodNames(initialPods);
        })();
    }, []);

    const appContext = useMemo(() => {
        const permissionsToFamilies = getPermissionsToFamilies(user);
        return {
            renderPageContent,
            user,
            customers,
            setCustomers,
            families,
            setFamilies,
            recentlySearchedIdsByType,
            setRecentlySearchedIdsByType,
            dateRangeSelectedOption,
            setDateRangeSelectedOption,
            permissionsToFamilies,
            userHasPermission: (permission, family) =>
                hasPermission(permissionsToFamilies, permission, family),
            podNames,
            setPodNames,
            regions,
            setRegions,
            customerSizes,
            setCustomerSizes,
            industries,
            setIndustries,
            segments,
            setSegments,
        };
    }, [
        user,
        renderPageContent,
        customers,
        families,
        recentlySearchedIdsByType,
        dateRangeSelectedOption,
        podNames,
        regions,
        customerSizes,
        industries,
        segments,
    ]);

    if (userIsLoading || !customers || !families) {
        return <Loading />;
    }

    // Outlet will serve any content that a nested <Route /> matches.
    return (
        <AppContext.Provider value={appContext}>
            <Outlet />
        </AppContext.Provider>
    );
}
